/**
 * Async actions for Settings Jira section (keeps component CRAP low).
 */

import type { JiraConnectionAck, UserSettingsPublic } from '../../services/clients/types';
import { jiraClient, providerOAuthClient } from '../../services/clients';

export async function loadJiraConnectionAck(
  setAck: (ack: JiraConnectionAck | null) => void,
): Promise<void> {
  try {
    const envelope = await jiraClient.getConnection();
    setAck(envelope.data);
  } catch {
    setAck(null);
  }
}

export async function runJiraConnectionTest(opts: {
  setBusy: (busy: boolean) => void;
  setError: (message: string | null) => void;
  setAck: (ack: JiraConnectionAck) => void;
}): Promise<void> {
  opts.setBusy(true);
  opts.setError(null);
  try {
    const envelope = await jiraClient.testConnection();
    opts.setAck(envelope.data);
    if (envelope.data.status === 'error') {
      opts.setError(envelope.data.error || 'La prueba de conexión falló');
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : '';
    opts.setError(
      /401|unauthorized|autenticat/i.test(message)
        ? 'Atlassian rechazó las credenciales. Verifica el Site URL, el email y el API token.'
        : message || 'No se pudo probar la conexión',
    );
  } finally {
    opts.setBusy(false);
  }
}

export async function startJiraOAuthFlow(opts: {
  setBusy: (busy: boolean) => void;
  setError: (message: string | null) => void;
}): Promise<void> {
  opts.setBusy(true);
  opts.setError(null);
  try {
    const envelope = await providerOAuthClient.start('jira');
    window.location.assign(envelope.data.authorizeUrl);
  } catch (err) {
    opts.setError(
      err instanceof Error
        ? err.message
        : 'OAuth no está disponible. Usa el token de API o configura JIRA_OAUTH_* en el servidor.',
    );
    opts.setBusy(false);
  }
}

export async function disconnectJiraOAuthFlow(opts: {
  setBusy: (busy: boolean) => void;
  setError: (message: string | null) => void;
  onSettingsRefresh?: (data: UserSettingsPublic) => void;
  reloadAck: () => Promise<void>;
}): Promise<void> {
  opts.setBusy(true);
  opts.setError(null);
  try {
    const envelope = await providerOAuthClient.disconnect('jira');
    opts.onSettingsRefresh?.(envelope.data);
    await opts.reloadAck();
  } catch (err) {
    opts.setError(err instanceof Error ? err.message : 'No se pudo desconectar OAuth');
  } finally {
    opts.setBusy(false);
  }
}
